// src/hooks/useReferenceNcs.ts — SCE Issues
// Le N° d'une NC-S envoyée, cherché dans le registre de la qualité (06/10).
//
// À l'ouverture d'une fiche qui a envoyé une NC-S sans en connaître encore le
// N°, la recherche part toute seule (api/registreNcs) ; trouvé sans doute
// possible, le N° est ÉCRIT sur le problème (`ecrire`, fourni par la fiche :
// référence + activité). Sinon les lignes possibles sont proposées.
// « Chercher à nouveau » relance à la main, à tout moment.
//
// En bascule de rôle, rien ne s'écrit tout seul : le N° est proposé.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  RegistreInaccessible,
  avecVerrou,
  declarationEnvoyee,
  inaccessibleAujourdhui,
  lignesRegistreNcs,
  noterInaccessible,
} from '@/api/registreNcs.api'
import { chercherDansRegistre, type Candidat } from '@/utils/registreNcs'

export type EtatReferenceNcs =
  | { etat: 'inactif' }
  | { etat: 'recherche' }
  | { etat: 'absente' }
  | { etat: 'a-choisir'; candidats: Candidat[] }
  | { etat: 'inaccessible' }
  | { etat: 'erreur'; message: string }

export interface DeclarationAChercher {
  issueId: string
  fichier: string
  /** Date ISO de l'événement « NC-S envoyée à la qualité ». */
  date: string
  /** Auteur de l'événement (repli si le formulaire ne se lit pas). */
  auteur: string
}

export interface UseReferenceNcsOptions {
  /** null : rien à chercher (N° déjà connu, pas de NC-S envoyée, fournisseur…). */
  declaration: DeclarationAChercher | null
  /** N° déjà portés par d'autres problèmes de l'appli. */
  prises: ReadonlySet<string>
  /** Écrit le N° sur le problème ; `auto` = trouvé seul. */
  ecrire: (candidat: Candidat, auto: boolean) => Promise<void>
  /** Bascule de rôle : proposer, ne jamais écrire seul. */
  lectureSeule: boolean
}

export interface UseReferenceNcsReturn {
  etat: EtatReferenceNcs
  chercher: () => void
  choisir: (candidat: Candidat) => Promise<void>
}

/** Jour local d'une date ISO. */
function jourDe(iso: string): string {
  const d = new Date(iso)
  const p = (n: number): string => String(n).padStart(2, '0')
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function useReferenceNcs({ declaration, prises, ecrire, lectureSeule }: UseReferenceNcsOptions): UseReferenceNcsReturn {
  const [etat, setEtat] = useState<EtatReferenceNcs>({ etat: 'inactif' })
  // Les valeurs du dernier rendu, lues par la recherche sans la relancer.
  const courant = useRef({ declaration, prises, ecrire, lectureSeule })
  courant.current = { declaration, prises, ecrire, lectureSeule }
  const derniere = useRef(0)

  const lancer = useCallback(async (manuelle: boolean): Promise<void> => {
    const { declaration: d } = courant.current
    if (!d) return
    if (!manuelle && inaccessibleAujourdhui()) return
    const numero = ++derniere.current
    const aJour = (): boolean => numero === derniere.current
    setEtat({ etat: 'recherche' })
    try {
      // Même verrou que la recherche de fond (features/reference-ncs) : si elle
      // traite ce problème, on l'attend — et on trouve le N° déjà écrit.
      const resultat = await avecVerrou(d.issueId, async () => {
        const [{ lignes }, envoyee] = await Promise.all([
          lignesRegistreNcs(),
          declarationEnvoyee(d.fichier, d.issueId, jourDe(d.date), d.auteur),
        ])
        const r = chercherDansRegistre(envoyee, lignes, courant.current.prises)
        if (r.etat === 'trouvee' && !courant.current.lectureSeule && aJour()) {
          await courant.current.ecrire(r.candidat, true)
        }
        return r
      })
      if (!aJour()) return
      if (resultat.etat === 'trouvee' && !courant.current.lectureSeule) {
        setEtat({ etat: 'inactif' })
        return
      }
      if (resultat.etat === 'trouvee') setEtat({ etat: 'a-choisir', candidats: [resultat.candidat] })
      else if (resultat.etat === 'a-choisir') setEtat({ etat: 'a-choisir', candidats: resultat.candidats })
      else setEtat({ etat: 'absente' })
    } catch (err) {
      if (!aJour()) return
      if (err instanceof RegistreInaccessible) {
        noterInaccessible()
        // En automatique, on se tait : la plupart n'ont pas accès au site qualité.
        setEtat(manuelle ? { etat: 'inaccessible' } : { etat: 'inactif' })
        return
      }
      console.error('[useReferenceNcs] recherche du N° NC-S :', err)
      setEtat(manuelle ? { etat: 'erreur', message: err instanceof Error ? err.message : 'Recherche impossible.' } : { etat: 'inactif' })
    }
  }, [])

  // Recherche automatique : à l'ouverture, et quand la NC-S à chercher change.
  const cle = declaration ? `${declaration.issueId}|${declaration.fichier}` : ''
  useEffect(() => {
    if (!cle) {
      derniere.current++
      setEtat({ etat: 'inactif' })
      return
    }
    void lancer(false)
  }, [cle, lancer])

  const chercher = useCallback(() => void lancer(true), [lancer])

  const choisir = useCallback(async (candidat: Candidat): Promise<void> => {
    setEtat({ etat: 'recherche' })
    try {
      await courant.current.ecrire(candidat, false)
      setEtat({ etat: 'inactif' })
    } catch (err) {
      setEtat({ etat: 'erreur', message: err instanceof Error ? err.message : "Le N° n'a pas pu être enregistré." })
    }
  }, [])

  return { etat, chercher, choisir }
}
